import {gsap} from "gsap";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {BruteForce} from "../BruteForce";
import {BruteForceGameAssets} from "../BruteForceGameAssets";
import {BelowStakeWinRestriction} from "../server/BelowStakeWinRestriction";
import {BruteForceGameConfig} from "../BruteForceGameConfig";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {BruteForceSound} from "../sound/BruteForceSound";
import {BLEND_MODES} from "pixi.js";
import {BruteForceHelper} from "../utils/BruteForceHelper";
import {ParticleController} from "../components/particles/ParticleController";
import {ParticleSettings} from "../components/particles/ParticleSettings";

export class BruteForceWinCapView extends PIXI.Container {
    private _blackBG: PIXI.Graphics;
    private _countUp: BasicBitmapCountUp;
    private _wincapTimes: PIXI.BitmapText;
    private _endCreditsContainer: PIXI.Container;
    private _creditsList: PIXI.Container;
    private _jobTitle: string[] = ["-Bad Guy #1-", "-Bad Guy #2-", "-Bad Guy #3-", "-Bad Guy #4-", "-Bad Guy #5-", "-Joshua-", "-Jason-", "-Hyper-"];
    private _name: string[][] = [["DeeeP , AjQ"], ["ShyAL, AjGee"], ["PreePan"], ["Reay"], ["EitsJay"], ["JayeL"], ["VajSi, Cheeku"], ["KayVee"]];
    private _redFrame: PIXI.Sprite;
    private _endLoopTl: gsap.core.Timeline | null;
    private _crackBg: PIXI.Sprite;
    private _crackedGlass: PIXI.spine.Spine;
    private _gameOverText: PIXI.Sprite;
    private _gameOverTextBottomGlow: PIXI.Sprite;
    private _maxWinOverlay: PIXI.Graphics;
    private _maxWinText: PIXI.spine.Spine;
    private _wincapFountainContainer: PIXI.Container;
    private _particleLoops: ParticleController[] = [];

    constructor() {
        super();
        this.name = "WinCapView";
        this.init();
    }

    public hideWinCap(): gsap.core.Timeline {
        const hideWinCapTL: gsap.core.Timeline = gsap.timeline();
        hideWinCapTL.to([this, this._blackBG, this._wincapTimes, this._gameOverText, this._gameOverTextBottomGlow, this._crackedGlass], {
            alpha: 0,
            duration: 0.2
        });
        hideWinCapTL.add(() => {
            BruteForceSound.switchMusic({name: BruteForceSound.MAIN_GAME_MUSIC});
            BruteForceSound.playTrack(BruteForceSound.MAIN_GAME_MUSIC_CRUSHED, 0);
            BruteForceHelper.resetSpineItem(this._maxWinText);
            BruteForceHelper.resetSpineItem(this._crackedGlass);
            this.visible = false;
            this.showViewElements(false);
            this.endCreditsElementsVisibility(false);
            this._endLoopTl!.totalProgress(1);
            this._endLoopTl!.kill();
            this._endLoopTl = null;
            BruteForce.backgroundElements.setMode("NORMAL");
            BruteForce.allreelsView.changeReelSet();
            BruteForce.totalWinView.showCountUpPostWincap();
        }, ">");
        return hideWinCapTL;
    }

    public show(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            totalWin: number = BruteForce.model.gameData.accumulatedRoundWin;

        tl.add(() => {
            BruteForce.keypadPlugin.setWin(0);
            this._countUp.stopAt(0);
            this._countUp.scale.set(0.5, 0.5);
            this._countUp.position.set(360, 490);

            this._maxWinText.alpha = 1;
            this.visible = true;
            this.alpha = 1;
            this._crackedGlass.alpha = 1;
            this._blackBG.alpha = 1;
            this.addParticles(this._wincapFountainContainer);
            this.endCreditsElementsVisibility(true);
            this.showViewElements(true);

        }, 0);

        tl.add(BruteForce.bigWinView.hide().timeScale(1.6), 0);
        tl.add(() => {
            this._maxWinText.state.setAnimation(0, "start", false);
            this._maxWinText.state.addAnimation(0, "loop", true, this._maxWinText.skeleton.data.findAnimation("start").duration);
        }, ">");
        tl.to(this, {duration: 2});
        tl.to([this._creditsList, this._redFrame], {alpha: 1, duration: 0.5});
        tl.to(this._creditsList, {duration: 2});
        tl.add([
            gsap.to(this._countUp, 0.1, {alpha: 1}),
            gsap.to(this._countUp.scale, 0.1, {x: 1, y: 1, ease: "sine.in"}),
            this._countUp.getCountAnimation(0, totalWin, 12),
        ], ">");
        tl.add(this.creditsLoop(), "<");
        tl.add(() => {
            BruteForceHelper.playSpineWithTimeline(this._crackedGlass, 0, "stage_1")
        }, "<0.5");

        tl.add(() => {
            BruteForceHelper.playSpineWithTimeline(this._crackedGlass, 0, "stage_2")
        }, 9.59);

        tl.add(() => {
            BruteForceHelper.playSpineWithTimeline(this._crackedGlass, 0, "stage_3")
        }, 11.59);

        tl.add(() => {
            BruteForceHelper.playSpineWithTimeline(this._crackedGlass, 0, "stage_4")
        }, 14.09);
        tl.add(() => {
            BruteForceHelper.playSpineWithTimeline(this._crackedGlass, 0, "stage_5")
        }, 16.59);

        tl.to(this, {duration: 0.5});
        tl.to([this._countUp, this._redFrame, this._maxWinText], {alpha: 0, duration: 0.2});
        tl.add(BruteForceHelper.playSpineWithTimeline(this._crackedGlass, 0, "stage_6"), "<");
        tl.to([this._gameOverText, this._gameOverTextBottomGlow], {alpha: 1, duration: 0.1}, "<");
        tl.add(() => {
            this.removeParticleFountain();
            this.endLoop();
        }, "<");
        tl.add(() => {
            BruteForce.gameContainer.screenShake(0.5, 150, false, 30);
        }, ">");

        tl.add(() => {
            BruteForce.keypadPlugin.setWin(BruteForce.model.gameData.accumulatedRoundWin, true, BelowStakeWinRestriction.isWinBelowStake());
        });

        tl.add(() => {
            BruteForceSound.switchMusic({name: BruteForceSound.WINCAP_MUSIC_GAMEOVER, volume: 0});
            BruteForceSound.fadeMusic(0, 0);
            BruteForceSound.fadeMusic(1, 15000);
        }, 22.09);


        // emit coin and other Particles at specified time
        tl.add(() => {
            this.emitFountain()
        }, 6);


        // console.log("wincap",tl.totalDuration()); 22.09
        return tl;
    }

    public emitFountain(): void {
        this._particleLoops.forEach((p: ParticleController) => {
            p.enableEmit(false);
        });

        this._particleLoops[0].enableEmit(true);
        this._particleLoops[1].enableEmit(true);
        this._particleLoops[2].enableEmit(true);
        this._particleLoops[3].enableEmit(true);
    }

    private init(): void {
        this.position.set(0, 70);

        this._countUp = new BasicBitmapCountUp({
            fontName: BruteForceGameAssets.BITMAP_WINCAP_COUNT_UP,
            fontSize: 18,
        }, 520, new PIXI.Point(0.5, 0.5));
        this._countUp.position.set(360, 490);
        this._countUp.stopAt(0);
        this._countUp.visible = false;
        this._countUp.alpha = 0;
        this._countUp.name = "wincapCountup";

        this._wincapTimes = new PIXI.BitmapText("40000x", {
            fontName: BruteForceGameAssets.BITMAP_WINCAP_COUNT_UP,
            fontSize: 18
        });
        this._wincapTimes.position.set(170, 446);
        this._wincapTimes.alpha = 0;
        this._wincapTimes.visible = false;
        this._wincapTimes.name = "wincapTimesValue";

        this._blackBG = new PIXI.Graphics();
        this._blackBG.name = "blackBG";
        this._blackBG.position.set(BruteForceGameConfig.reelSetPositionX, BruteForceGameConfig.reelSetPositionY);
        this._blackBG.visible = false;

        this._maxWinOverlay = new PIXI.Graphics();
        this._maxWinOverlay.name = "maxwinoverlay";
        this._maxWinOverlay.position.set(BruteForceGameConfig.reelSetPositionX, BruteForceGameConfig.reelSetPositionY);
        this._maxWinOverlay.visible = false;
        this._maxWinOverlay.alpha = 0.5;

        this._maxWinText = new PIXI.spine.Spine(BruteForce.assetLoader.getSpineAsset("maxWinText"));
        this._maxWinText.name = "maxwinBG";
        this._maxWinText.position.set(359, 279);
        this._maxWinText.height = 574;
        this._maxWinText.width = 720;
        this._maxWinText.alpha = 0;
        this._maxWinText.visible = false;


        this._redFrame = new PIXI.Sprite(BruteForce.assetLoader.getTextures("credits_frame")[0]);
        this._redFrame.position.set(BruteForceGameConfig.reelSetPositionX - 11, BruteForceGameConfig.reelSetPositionY - 100);
        this._redFrame.name = "redFrame";
        this._redFrame.width = 600;
        this._redFrame.height = 400;
        this._redFrame.alpha = 0;


        this.visible = false;
        this.addChild(this._blackBG, this._maxWinText, this._maxWinOverlay);

        this.createCrackedScreenAssets();

        this.addChild(this._redFrame);
        this.endCreditsAssets();

        this.addChild(this._wincapTimes);

        this.addChild(this._crackedGlass);
        this._wincapFountainContainer = new PIXI.Container();
        this._wincapFountainContainer.name = "fountainContainer";

        this.addChild(this._wincapFountainContainer);
        this.addChild(this._countUp);


        this.bgSize();
        this.showViewElements(false);

    }

    private bgSize() {
        this._blackBG.beginFill(0x000000, 1);
        this._blackBG.drawRect(-72, -128, 720, 576);
        this._blackBG.endFill();

        this._maxWinOverlay.beginFill(0x00000, 1);
        this._maxWinOverlay.drawRect(-72, -128, 720, 576);
        this._maxWinOverlay.endFill();
    }

    private endCreditsAssets(): void {

        this._endCreditsContainer = new PIXI.Container();
        this._endCreditsContainer.name = "Credits";
        this._endCreditsContainer.position.set(BruteForceGameConfig.reelSetPositionX, BruteForceGameConfig.reelSetPositionY);
        this._creditsList = new PIXI.Container();

        this._creditsList.name = "creditList";
        this._creditsList.position.set(45, -70);
        this._creditsList.alpha = 0;
        this._endCreditsContainer.addChild(this._creditsList);

        let memberCount: number = 0;
        this._jobTitle.forEach((title: string, index: number) => {
            const oneDescriptionContainer: PIXI.Container = new PIXI.Container();
            if (index === 0) {
                oneDescriptionContainer.position.set(0, 430);
            } else {
                oneDescriptionContainer.position.set(0, this._creditsList.children[index - 1].position.y + 181);
            }
            const jobTitle: PIXI.Text = new PIXI.Text(this._jobTitle[index], new PIXI.TextStyle(
                {
                    fill: "#ffffff",
                    fontFamily: "ARCADECLASSIC",
                    wordWrap: false,
                    fontWeight: FontWeight.BOLD,
                    fontSize: 36,
                    letterSpacing: 5,
                }));

            jobTitle.position.set(250 - jobTitle.width * 0.5, 0);
            jobTitle.name = `jobTitle${index}`;
            oneDescriptionContainer.addChild(jobTitle);

            this._name[index].forEach((oneName: string, personNo: number) => {
                const nameOfPerson: PIXI.Text = new PIXI.Text(this._name[index][personNo], new PIXI.TextStyle(
                    {
                        fill: "#ef1250",
                        fontFamily: "ARCADECLASSIC",
                        wordWrap: false,
                        fontWeight: FontWeight.BOLD,
                        fontSize: 30,
                        letterSpacing: 5,
                    }));
                nameOfPerson.name = `name${index}_${personNo}`;
                nameOfPerson.position.set(160, 81 + 100 * personNo);
                oneDescriptionContainer.addChild(nameOfPerson);

                memberCount++;


                const photoOfPerson: PIXI.Sprite = new PIXI.Sprite(BruteForce.assetLoader.getTextures(`team_member${memberCount}`)[0]);
                photoOfPerson.position.set(50, 45 + 100 * personNo);

                photoOfPerson.name = `photo${index}`;
                oneDescriptionContainer.addChild(photoOfPerson);
            });

            this._creditsList.addChild(oneDescriptionContainer);

        });

        const creditMask: PIXI.Graphics = new PIXI.Graphics();
        creditMask.beginFill(0x00C5FF, 1);
        creditMask.drawRect(-5, 0, 600, 390);
        creditMask.endFill();
        creditMask.position.set(10, -95);
        this._endCreditsContainer.addChild(creditMask);
        this._endCreditsContainer.mask = creditMask;

        this.endCreditsElementsVisibility(false);
        this.addChild(this._endCreditsContainer);
        this.endGameMessage();
    }

    private endGameMessage(): void {

        const messageTextStyle: PIXI.TextStyle = new PIXI.TextStyle({
            fill: "#ffff00",
            fontFamily: "ARCADECLASSIC",
            wordWrap: false,
            align: "center",
            fontSize: 100,
            letterSpacing: 5,
        })

        const lineTextStyle: PIXI.TextStyle = new PIXI.TextStyle({
            fill: "#ffffff",
            fontFamily: "ARCADECLASSIC",
            wordWrap: true,
            wordWrapWidth: 600,
            align: "center",
            fontSize: 40,
            letterSpacing: 5,
        });

        const messageTitleContainer: PIXI.Container = new PIXI.Container();
        messageTitleContainer.name = "messageContainer";
        const messageTitle: PIXI.Text = new PIXI.Text("CONGRATULATIONS!", messageTextStyle);
        messageTitle.position.set(10, 0);
        messageTitle.scale.set(0.5, 1);
        messageTitle.name = "congrats";

        const line: PIXI.Text = new PIXI.Text("YOU'VE DESTROYED HYPER AND HIS NEST!", lineTextStyle);
        line.name = "line_1";
        line.position.set(-25, 105);

        const line_2: PIXI.Text = new PIXI.Text("THE UNIVERSE IS SAFE.", lineTextStyle);
        line_2.name = "line_2";
        line_2.position.set(-5, 195);

        const line_3: PIXI.Text = new PIXI.Text("CONSIDER YOURSELF A HERO.", lineTextStyle);
        line_3.name = "line_3";
        line_3.position.set(5, 255);

        messageTitleContainer.addChild(messageTitle, line, line_2, line_3);
        this._creditsList.addChild(messageTitleContainer);
    }

    private endCreditsElementsVisibility(toSetVisible: boolean): void {
        this._creditsList.children.forEach((item: PIXI.DisplayObject) => {
            item.visible = toSetVisible;
        });
    }

    private creditsLoop(): gsap.core.Timeline {
        const creditsLoopTl: gsap.core.Timeline = gsap.timeline();
        creditsLoopTl.to(this._creditsList, {
            duration: 15.2,
            y: -1850,
            ease: "none"
        }, 0);
        creditsLoopTl.add(() => {
            this._creditsList.alpha = 0;
            this._creditsList.position.set(45, -70);
        });

        return creditsLoopTl;
    }

    private createCrackedScreenAssets(): void {
        this._gameOverText = new PIXI.Sprite(BruteForce.assetLoader.getTextures("gameOverText")[0]);
        this._gameOverText.anchor.set(0.5, 0.5);
        this._gameOverText.position.set(375, BruteForceGameConfig.reelSetPositionY * 2 + 10);
        this._gameOverText.name = "gameoverText";
        this._gameOverText.visible = false;
        this._gameOverText.alpha = 0;

        this._gameOverTextBottomGlow = new PIXI.Sprite(BruteForce.assetLoader.getTextures("gameOverTextBottomGlow")[0]);
        this._gameOverTextBottomGlow.anchor.set(0.5, 0.5);
        this._gameOverTextBottomGlow.scale.set(1, 0.5);
        this._gameOverTextBottomGlow.position.set(360, BruteForceGameConfig.reelSetPositionY * 2 + 50);
        this._gameOverTextBottomGlow.name = "glowLight";
        this._gameOverTextBottomGlow.blendMode = BLEND_MODES.ADD;
        this._gameOverTextBottomGlow.visible = false;
        this._gameOverTextBottomGlow.alpha = 0;

        this._crackedGlass = new PIXI.spine.Spine(BruteForce.assetLoader.getSpineAsset("crackedGlass"));
        this._crackedGlass.position.set(360, 280);
        this._crackedGlass.name = "glassCracked";
        this._crackedGlass.visible = false;

        this.addChild(this._gameOverTextBottomGlow, this._gameOverText, this._crackedGlass);

    }

    private endLoop(): gsap.core.Timeline {
        this._endLoopTl = gsap.timeline({repeat: -1});
        this._endLoopTl.to([this._gameOverText, this._gameOverTextBottomGlow, this._wincapTimes], {
            alpha: 1,
            duration: 0.5
        });
        this._endLoopTl.to([this._gameOverText, this._gameOverTextBottomGlow, this._wincapTimes], {
            alpha: 0,
            duration: 0.5
        });
        return this._endLoopTl;
    }

    private showViewElements(setVisibilty: boolean): void {
        this.children.forEach((item: PIXI.DisplayObject) => {
            item.visible = setVisibilty;
        });
    }

    private addParticles(container: PIXI.Container): void {
        this._particleLoops.push(BruteForce.particleManager.createAnimation(container, "coinSilverWinCapLoop", "coinSilver", ParticleSettings.wincapCoinConfigs, true));
        this._particleLoops.push(BruteForce.particleManager.createAnimation(container, "coinGoldWincapLoop", "coinGold", ParticleSettings.wincapCoinConfigs, true));
        this._particleLoops.push(BruteForce.particleManager.createAnimation(container, "bulletWincapLoop", "bullet", ParticleSettings.wincapNonCoinConfigs, true));
        this._particleLoops.push(BruteForce.particleManager.createAnimation(container, "grenadeWincapLoop", "grenade", ParticleSettings.wincapNonCoinConfigs, true));

    }

    private removeParticleFountain(): void {
        this._particleLoops.forEach((p: ParticleController) => {
            p.enableEmit(false);
        });
        this._particleLoops.forEach((particleController) => {
            particleController.remove();
        });
        this._particleLoops = [];
    }
}